/**
 * Health Recommendation Card Component
 * Displays individual health recommendation with severity and source
 */

import React from "react";
import { AlertTriangle, Info, Shield, AlertCircle } from "lucide-react";
import type { HealthRecommendation } from "@/services/api/healthRecommendations";
import { useI18nStore } from "@/stores/i18nStore";
import { useLocaleFormat } from "@/hooks/useLocaleFormat";

interface HealthRecommendationCardProps {
  recommendation: HealthRecommendation;
}

export const HealthRecommendationCard: React.FC<
  HealthRecommendationCardProps
> = ({ recommendation }) => {
  const t = useI18nStore((state) => state.t);
  const fmt = useLocaleFormat();

  const severityLabels: Record<string, string> = {
    critical: t.recsSeverityCritical,
    high: t.recsSeverityHigh,
    medium: t.recsSeverityMedium,
    low: t.recsSeverityLow,
    info: t.recsSeverityInfo,
  };
  const severityLabel = (
    severityLabels[recommendation.severity] ?? recommendation.severity
  ).toLocaleUpperCase(fmt.locale);

  const getSeverityConfig = (severity: string) => {
    switch (severity) {
      case "critical":
        return {
          bg: "bg-red-50",
          border: "border-red-200",
          icon: <AlertTriangle className="h-5 w-5 text-red-600" />,
          badge: "bg-red-100 text-red-800",
        };
      case "high":
        return {
          bg: "bg-orange-50",
          border: "border-orange-200",
          icon: <AlertCircle className="h-5 w-5 text-orange-600" />,
          badge: "bg-orange-100 text-orange-800",
        };
      case "medium":
        return {
          bg: "bg-yellow-50",
          border: "border-yellow-200",
          icon: <Info className="h-5 w-5 text-yellow-600" />,
          badge: "bg-yellow-100 text-yellow-800",
        };
      case "low":
        return {
          bg: "bg-primary-50",
          border: "border-primary-200",
          icon: <Info className="h-5 w-5 text-primary-600" />,
          badge: "bg-primary-100 text-primary-800",
        };
      default:
        return {
          bg: "bg-gray-50",
          border: "border-gray-200",
          icon: <Info className="h-5 w-5 text-gray-600" />,
          badge: "bg-gray-100 text-gray-800",
        };
    }
  };

  const getCategoryIcon = (category: string) => {
    switch (category) {
      case "vaccination":
        return "💉";
      case "medication":
        return "💊";
      case "precaution":
        return "⚠️";
      case "advisory":
        return "ℹ️";
      case "warning":
        return "🚨";
      default:
        return "📋";
    }
  };

  const getEvidenceLevelBadge = (level: string) => {
    switch (level) {
      case "mandatory":
        return (
          <span className="px-2 py-1 bg-red-100 text-red-800 text-xs font-medium rounded">
            {t.mandatory}
          </span>
        );
      case "recommended":
        return (
          <span className="px-2 py-1 bg-orange-100 text-orange-800 text-xs font-medium rounded">
            {t.recommended}
          </span>
        );
      case "suggested":
        return (
          <span className="px-2 py-1 bg-yellow-100 text-yellow-800 text-xs font-medium rounded">
            {t.suggested}
          </span>
        );
      default:
        return (
          <span className="px-2 py-1 bg-gray-100 text-gray-800 text-xs font-medium rounded">
            {t.optional}
          </span>
        );
    }
  };

  const config = getSeverityConfig(recommendation.severity);

  return (
    <div className={`${config.bg} border ${config.border} rounded-lg p-4`}>
      <div className="flex items-start gap-3">
        <div className="flex-shrink-0">{config.icon}</div>

        <div className="flex-1">
          {/* Header */}
          <div className="flex items-start justify-between mb-2">
            <div className="flex items-center gap-2">
              <span className="text-xl">
                {getCategoryIcon(recommendation.category)}
              </span>
              <h3 className="text-lg font-semibold text-gray-900">
                {recommendation.title}
              </h3>
            </div>
            <div className="flex items-center gap-2">
              {getEvidenceLevelBadge(recommendation.evidenceLevel)}
              <span
                className={`px-2 py-1 text-xs font-medium rounded ${config.badge}`}
              >
                {severityLabel}
              </span>
            </div>
          </div>

          {/* Description */}
          <p className="text-gray-700 mb-2">{recommendation.description}</p>

          {/* Details */}
          {recommendation.details && (
            <div className="mt-3 p-3 bg-white rounded border border-gray-200">
              <p className="text-sm text-gray-600">{recommendation.details}</p>
            </div>
          )}

          {/* Related Diseases */}
          {recommendation.relatedDiseases &&
            recommendation.relatedDiseases.length > 0 && (
              <div className="mt-3">
                <div className="text-xs font-medium text-gray-500 mb-1">
                  {t.relatedDiseases}:
                </div>
                <div className="flex flex-wrap gap-1">
                  {recommendation.relatedDiseases.map((disease, index) => (
                    <span
                      key={index}
                      className="px-2 py-1 bg-white border border-gray-200 text-xs text-gray-700 rounded"
                    >
                      {disease}
                    </span>
                  ))}
                </div>
              </div>
            )}

          {/* Footer */}
          <div className="mt-3 flex items-center justify-between text-xs text-gray-500">
            <div className="flex items-center gap-1">
              <Shield className="h-3 w-3" />
              <span>
                {t.source}: {recommendation.source.name}
              </span>
            </div>
            <div>
              {t.updated}: {fmt.date(recommendation.lastUpdated)}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};
